import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { CalendarBlank, DownloadSimple, FunnelSimple, X } from "@phosphor-icons/react";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUS_COLS = [
  { key: "present", label: "P", color: "text-emerald-700", title: "Present" },
  { key: "half_day", label: "½", color: "text-amber-600", title: "Half day" },
  { key: "absent", label: "A", color: "text-destructive", title: "Absent" },
  { key: "week_off", label: "WO", color: "text-ink-muted", title: "Week off" },
  { key: "holiday", label: "H", color: "text-sky-600", title: "Holiday" },
  { key: "leave", label: "L", color: "text-violet-600", title: "Leave" },
  { key: "lop", label: "LOP", color: "text-neutral-800", title: "Loss of pay" },
];

function fmtHM(mins) {
  const m = Number(mins) || 0;
  if (m <= 0) return "0";
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}

function firstDayOfMonth(year, month) {
  const d = new Date(Date.UTC(year, month, 1));
  return d.toISOString().slice(0, 10);
}
function lastDayOfMonth(year, month) {
  const d = new Date(Date.UTC(year, month + 1, 0));
  return d.toISOString().slice(0, 10);
}
function eachDay(fromISO, toISO) {
  const out = [];
  const s = new Date(fromISO);
  const e = new Date(toISO);
  const cur = new Date(s);
  while (cur <= e) {
    out.push(cur.toISOString().slice(0, 10));
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return out;
}

function DrillDownModal({ row, from, to, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState({});

  useEffect(() => {
    if (!row) return;
    (async () => {
      setLoading(true);
      try {
        const year = parseInt(from.slice(0, 4), 10);
        const [attRes, holRes] = await Promise.all([
          api.get("/hr/attendance", {
            params: { employee_id: row.employee_id, date_from: from, date_to: to },
          }),
          api.get("/hr/holidays", { params: { year } }),
        ]);
        setRows(attRes.data || []);
        const h = {};
        (holRes.data || []).forEach((x) => { h[x.date] = x; });
        setHolidays(h);
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [row, from, to]);

  if (!row) return null;
  const byDate = {};
  rows.forEach((r) => { byDate[r.date] = r; });
  const days = eachDay(from, to);

  return (
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-4" data-testid="hr-att-report-drilldown">
      <div className="bg-canvas border border-canvas-line max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-canvas border-b border-canvas-line px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Daily Attendance</div>
            <div className="text-lg font-semibold">{row.name} <span className="font-mono text-xs text-ink-muted">· {row.employee_code}</span></div>
            <div className="font-mono text-[10px] text-ink-muted">{row.branch_name || "—"} · {from} to {to}</div>
          </div>
          <button onClick={onClose} data-testid="hr-att-report-drilldown-close" className="p-2 border border-canvas-line hover:border-ink"><X size={14} weight="bold" /></button>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center font-mono text-xs text-ink-subtle py-8">Loading…</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-canvas-line bg-canvas-panel">
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Date</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Day</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Status</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">In · Out</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Late</th>
                  <th className="text-right px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">OT</th>
                  <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Note</th>
                </tr>
              </thead>
              <tbody>
                {days.map((d) => {
                  const rec = byDate[d];
                  const dObj = new Date(d);
                  const dayName = dObj.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
                  const isSun = dObj.getUTCDay() === 0;
                  const isHol = !!holidays[d];
                  const isNonWork = isSun || isHol;
                  const st = rec?.status || (isNonWork ? "week_off" : "—");
                  const meta = STATUS_COLS.find((c) => c.key === st);
                  return (
                    <tr key={d} className={`border-b border-canvas-line ${isNonWork ? "bg-amber-50/40" : ""}`}>
                      <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums">{d}</td>
                      <td className="px-3 py-1.5 text-xs">
                        {dayName}
                        {isHol && <span className="ml-1 text-[10px] text-amber-700">· {holidays[d].name}</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        {meta ? (
                          <span className={`font-mono text-[11px] font-semibold ${meta.color}`}>{meta.label}</span>
                        ) : (
                          <span className="font-mono text-[11px] text-ink-subtle">{st}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[11px] tabular-nums">
                        {rec?.check_in || "—"} · {rec?.check_out || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11px] text-destructive tabular-nums">
                        {rec?.late_minutes ? `${rec.late_minutes}m` : ""}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono text-[11px] text-emerald-700 tabular-nums">
                        {rec?.status === "present" && isNonWork ? "OT" : ""}
                      </td>
                      <td className="px-3 py-1.5 text-xs text-ink-muted">{rec?.notes || ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HrAttendanceReportPage() {
  const { user } = useAuth();
  const isAdminHR = user?.role === "admin" || (user?.permissions || []).includes("branches.view_all");

  const today = new Date();
  const [mode, setMode] = useState("month"); // "month" | "range"
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth(today.getFullYear(), today.getMonth()));
  const [dateTo, setDateTo] = useState(lastDayOfMonth(today.getFullYear(), today.getMonth()));
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(""); // "" = all
  // Two-tier view: null = show Branch Summary; branchName = show that
  // branch's employee grid. Kept separate from `branchId` (the top filter)
  // so an admin can e.g. filter by "AD" branches and then drill into one.
  const [drillBranchName, setDrillBranchName] = useState(null);
  // Restrict to only active employees on the report (user request 2026-02).
  // Toggleable so payroll re-runs / audits can widen back to everyone.
  const [activeOnly, setActiveOnly] = useState(true);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const [drillRow, setDrillRow] = useState(null);

  const effectiveFrom = mode === "month" ? firstDayOfMonth(year, month) : dateFrom;
  const effectiveTo = mode === "month" ? lastDayOfMonth(year, month) : dateTo;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: effectiveFrom, date_to: effectiveTo, active_only: activeOnly };
      if (isAdminHR && branchId) params.branch_id = branchId;
      const { data } = await api.get("/hr/attendance/report", { params });
      setRows(data.rows || []);
      setTotals(data.totals || {});
      // Reset drill-branch whenever the top-level filters change — otherwise
      // the user gets stuck in an empty drill view after switching months.
      setDrillBranchName(null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [effectiveFrom, effectiveTo, branchId, isAdminHR, activeOnly]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdminHR) return;
    api.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, [isAdminHR]);

  // Group rows by branch for display.
  const grouped = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const key = r.branch_name || "(no branch)";
      g[key] = g[key] || [];
      g[key].push(r);
    });
    return g;
  }, [rows]);

  // Per-branch summary rows — one row per branch aggregating the
  // per-employee counts. Powers the default "Branch Summary" view.
  const branchSummary = useMemo(() => {
    const summary = Object.entries(grouped).map(([branchName, brRows]) => {
      const agg = {
        branch_name: branchName,
        employee_count: brRows.length,
        present: 0, half_day: 0, absent: 0, week_off: 0, holiday: 0, leave: 0, lop: 0,
        late_count: 0, late_minutes: 0,
        ot_count: 0,
        payable_days: 0,
      };
      brRows.forEach((r) => {
        agg.present += Number(r.present) || 0;
        agg.half_day += Number(r.half_day) || 0;
        agg.absent += Number(r.absent) || 0;
        agg.week_off += Number(r.week_off) || 0;
        agg.holiday += Number(r.holiday) || 0;
        agg.leave += Number(r.leave) || 0;
        agg.lop += Number(r.lop) || 0;
        agg.late_count += Number(r.late_count) || 0;
        agg.late_minutes += Number(r.late_minutes) || 0;
        agg.ot_count += Number(r.ot_count) || 0;
        agg.payable_days += Number(r.payable_days) || 0;
      });
      return agg;
    });
    // Alphabetical for stable order.
    summary.sort((a, b) => a.branch_name.localeCompare(b.branch_name));
    return summary;
  }, [grouped]);

  const exportCsv = () => {
    if (rows.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const headers = [
      "Employee Code", "Name", "Branch", "Designation", "Work Area",
      "Present", "Half-day", "Absent", "Week Off", "Holiday", "Leave", "LOP",
      "Late Count", "Late Minutes", "OT Days", "Payable Days",
    ];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      const cells = [
        r.employee_code, r.name, r.branch_name || "", r.designation || "", r.work_area || "",
        r.present, r.half_day, r.absent, r.week_off, r.holiday, r.leave, r.lop,
        r.late_count, r.late_minutes, r.ot_count, r.payable_days,
      ].map((v) => {
        const s = String(v ?? "");
        return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-report_${effectiveFrom}_to_${effectiveTo}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  const yearOptions = [];
  for (let y = today.getFullYear() - 2; y <= today.getFullYear() + 1; y += 1) yearOptions.push(y);

  return (
    <div>
      <PageHeader
        eyebrow="HR / 05"
        title="Attendance Report"
        subtitle="Per-employee attendance summary by branch. Toggle Month or Date-range, filter by branch, click any row to drill into the daily grid."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={exportCsv}
              disabled={loading || rows.length === 0}
              data-testid="hr-att-report-export"
              className="px-4 py-2.5 border border-ink text-sm font-semibold hover:bg-canvas-panel disabled:opacity-40 flex items-center gap-2"
            >
              <DownloadSimple size={14} weight="bold" />
              Export CSV
            </button>
          </div>
        }
      />

      <div className="px-8 py-6 space-y-4">
        {/* Filters */}
        <div className="border border-canvas-line bg-canvas-card p-4 flex items-center gap-3 flex-wrap" data-testid="hr-att-report-filters">
          <FunnelSimple size={14} className="text-ink-muted" />

          {/* Mode toggle */}
          <div className="flex border border-canvas-line">
            <button
              onClick={() => setMode("month")}
              data-testid="hr-att-report-mode-month"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider ${mode === "month" ? "bg-ink text-white" : "hover:bg-canvas-panel"}`}
            >
              Month
            </button>
            <button
              onClick={() => setMode("range")}
              data-testid="hr-att-report-mode-range"
              className={`px-3 py-1.5 text-xs font-mono uppercase tracking-wider border-l border-canvas-line ${mode === "range" ? "bg-ink text-white" : "hover:bg-canvas-panel"}`}
            >
              Date range
            </button>
          </div>

          {mode === "month" ? (
            <>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                data-testid="hr-att-report-month"
                className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
              >
                {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value, 10))}
                data-testid="hr-att-report-year"
                className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
              >
                {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 border border-canvas-line px-2 py-1 bg-canvas">
                <CalendarBlank size={12} className="text-ink-muted" />
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="hr-att-report-from"
                  className="bg-transparent text-sm focus:outline-none"
                />
              </div>
              <span className="font-mono text-xs text-ink-muted">to</span>
              <div className="flex items-center gap-2 border border-canvas-line px-2 py-1 bg-canvas">
                <CalendarBlank size={12} className="text-ink-muted" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="hr-att-report-to"
                  className="bg-transparent text-sm focus:outline-none"
                />
              </div>
            </>
          )}

          {isAdminHR && (
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid="hr-att-report-branch"
              className="px-3 py-1.5 bg-canvas border border-canvas-line text-sm"
            >
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}

          <label className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-ink-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
              data-testid="hr-att-report-active-only"
              className="w-4 h-4"
            />
            Active employees only
          </label>

          <div className="ml-auto font-mono text-[10px] text-ink-muted">
            {loading
              ? "Loading…"
              : drillBranchName
                ? `${(grouped[drillBranchName] || []).length} in ${drillBranchName} · ${effectiveFrom} → ${effectiveTo}`
                : `${branchSummary.length} branch${branchSummary.length === 1 ? "" : "es"} · ${rows.length} employee${rows.length === 1 ? "" : "s"} · ${effectiveFrom} → ${effectiveTo}`}
          </div>
        </div>

        {/* Totals strip */}
        {rows.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-2" data-testid="hr-att-report-totals">
            {STATUS_COLS.map((c) => (
              <div key={c.key} className="border border-canvas-line bg-canvas-card p-2">
                <div className={`font-mono text-[9px] uppercase tracking-widest ${c.color}`}>{c.title}</div>
                <div className="font-display text-xl font-bold tabular-nums">{totals[c.key] || 0}</div>
              </div>
            ))}
            <div className="border border-canvas-line bg-canvas-card p-2">
              <div className="font-mono text-[9px] uppercase tracking-widest text-destructive">Late</div>
              <div className="font-display text-xl font-bold tabular-nums">{totals.late_count || 0}</div>
              <div className="font-mono text-[9px] text-ink-muted">{fmtHM(totals.late_minutes)}</div>
            </div>
            <div className="border border-canvas-line bg-canvas-card p-2">
              <div className="font-mono text-[9px] uppercase tracking-widest text-emerald-700">OT DAYS</div>
              <div className="font-display text-xl font-bold tabular-nums">{totals.ot_count || 0}</div>
              <div className="font-mono text-[9px] text-ink-muted">Sun/holiday work</div>
            </div>
          </div>
        )}

        {/* Per-branch tables */}
        {loading && <div className="text-center font-mono text-xs text-ink-subtle py-12">Loading report…</div>}
        {!loading && rows.length === 0 && (
          <div className="border border-canvas-line bg-canvas-card p-12 text-center font-mono text-xs text-ink-subtle">
            No employees found for the selected filters.
          </div>
        )}
        {/* --- Level 1: Branch summary (default view) --- */}
        {!loading && rows.length > 0 && !drillBranchName && (
          <div className="border border-canvas-line bg-canvas-card overflow-hidden" data-testid="hr-att-report-branch-summary">
            <div className="px-4 py-2.5 bg-canvas-panel border-b border-canvas-line flex items-center justify-between">
              <div className="text-sm font-semibold">Branch Summary</div>
              <div className="font-mono text-[10px] text-ink-muted">Click a branch to see its employees</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-canvas-line bg-canvas-panel/40">
                    <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Branch</th>
                    <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Emp.</th>
                    {STATUS_COLS.map((c) => (
                      <th key={c.key} className={`text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest ${c.color}`} title={c.title}>
                        {c.label}
                      </th>
                    ))}
                    <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-destructive" title="Number of days the employee arrived after (shift start + 15 min grace)">Late Days</th>
                    <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-destructive" title="Total minutes late across all late days">Late Mins</th>
                    <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-700" title="OT count · duration">OT</th>
                    <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-ink">Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {branchSummary.map((b) => (
                    <tr
                      key={b.branch_name}
                      onClick={() => setDrillBranchName(b.branch_name)}
                      data-testid={`hr-att-report-branch-row-${b.branch_name}`}
                      className="border-b border-canvas-line hover:bg-canvas-panel/30 cursor-pointer"
                    >
                      <td className="px-3 py-2 text-sm font-semibold">{b.branch_name}</td>
                      <td className="text-right px-2 py-2 font-mono text-[11px] tabular-nums font-semibold">{b.employee_count}</td>
                      {STATUS_COLS.map((c) => (
                        <td key={c.key} className="text-right px-2 py-2 font-mono text-[11px] tabular-nums">
                          {b[c.key] || ""}
                        </td>
                      ))}
                      <td className="text-right px-2 py-2 font-mono text-[11px] text-destructive tabular-nums font-semibold">
                        {b.late_count || ""}
                      </td>
                      <td className="text-right px-2 py-2 font-mono text-[11px] text-destructive tabular-nums">
                        {b.late_minutes ? fmtHM(b.late_minutes) : ""}
                      </td>
                      <td className="text-right px-2 py-2 font-mono text-[11px] text-emerald-700 tabular-nums">
                        {b.ot_count ? `${b.ot_count}·${fmtHM(b.ot_minutes)}` : ""}
                      </td>
                      <td className="text-right px-2 py-2 font-mono text-[11px] font-semibold tabular-nums">{b.payable_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* --- Level 2: Employee grid for the drilled branch --- */}
        {!loading && drillBranchName && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => setDrillBranchName(null)}
              data-testid="hr-att-report-back-to-branches"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink"
            >
              ← Back to all branches
            </button>
            <div className="border border-canvas-line bg-canvas-card overflow-hidden" data-testid={`hr-att-report-branch-${drillBranchName}`}>
              <div className="px-4 py-2.5 bg-canvas-panel border-b border-canvas-line flex items-center justify-between">
                <div className="text-sm font-semibold">{drillBranchName}</div>
                <div className="font-mono text-[10px] text-ink-muted">{(grouped[drillBranchName] || []).length} employee{(grouped[drillBranchName] || []).length === 1 ? "" : "s"}</div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-canvas-line bg-canvas-panel/40">
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Code</th>
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Name</th>
                      <th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-ink-muted">Work Area</th>
                      {STATUS_COLS.map((c) => (
                        <th key={c.key} className={`text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest ${c.color}`} title={c.title}>
                          {c.label}
                        </th>
                      ))}
                      <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-destructive" title="Number of days the employee arrived after (shift start + 15 min grace)">Late Days</th>
                      <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-destructive" title="Total minutes late across all late days">Late Mins</th>
                      <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-700" title="OT count · duration">OT</th>
                      <th className="text-right px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-ink">Payable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grouped[drillBranchName] || []).map((r) => (
                      <tr
                        key={r.employee_id}
                        onClick={() => setDrillRow(r)}
                        data-testid={`hr-att-report-row-${r.employee_id}`}
                        className="border-b border-canvas-line hover:bg-canvas-panel/30 cursor-pointer"
                      >
                        <td className="px-3 py-1.5 font-mono text-[11px]">{r.employee_code}</td>
                        <td className="px-3 py-1.5">
                          <div className="text-sm font-semibold">{r.name}</div>
                          {r.designation && <div className="font-mono text-[9px] text-ink-muted">{r.designation}</div>}
                        </td>
                        <td className="px-3 py-1.5 text-xs">{r.work_area || "—"}</td>
                        {STATUS_COLS.map((c) => (
                          <td key={c.key} className="text-right px-2 py-1.5 font-mono text-[11px] tabular-nums">
                            {r[c.key] || ""}
                          </td>
                        ))}
                        <td className="text-right px-2 py-1.5 font-mono text-[11px] text-destructive tabular-nums font-semibold">
                          {r.late_count || ""}
                        </td>
                        <td className="text-right px-2 py-1.5 font-mono text-[11px] text-destructive tabular-nums">
                          {r.late_minutes ? `${r.late_minutes}m` : ""}
                        </td>
                        <td className="text-right px-2 py-1.5 font-mono text-[11px] text-emerald-700 tabular-nums">
                          {r.ot_count || ""}
                        </td>
                        <td className="text-right px-2 py-1.5 font-mono text-[11px] font-semibold tabular-nums">{r.payable_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      <DrillDownModal row={drillRow} from={effectiveFrom} to={effectiveTo} onClose={() => setDrillRow(null)} />
    </div>
  );
}
